import React, { useEffect } from 'react';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { EMAIL_REGEX } from '../utils/regex';

interface LoginFormValues {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    clearError();
  }, [clearError]);

  const form = useForm({
    initialValues: {
      email: '',
      password: ''
    },
    validate: {
      email: (value) => (EMAIL_REGEX.test(value) ? null : 'Invalid email format'),
      password: (value) => (value.length < 1 ? 'Password is required' : null)
    }
  });

  const handleSubmit = async (values: LoginFormValues) => {
    await login(values.email, values.password);
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      <Container size="xs" style={{ width: '100%' }}>
        <Center>
          <Paper
            p="xl"
            radius="md"
            shadow="xl"
            style={{
              width: '100%',
              maxWidth: 400,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              marginTop: '20px',
              marginBottom: '20px'
            }}
          >
            <Stack gap="lg">
              <div style={{ textAlign: 'center' }}>
                <Title c="dark" mb="xs" order={2}>
                  Welcome Back
                </Title>
                <Text c="dimmed" size="sm">
                  Sign in to your account to continue
                </Text>
              </div>

              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                  <TextInput
                    required
                    label="Email"
                    name="email"
                    placeholder="Enter your email"
                    size="md"
                    type="email"
                    {...form.getInputProps('email')}
                  />

                  <PasswordInput
                    required
                    label="Password"
                    name="password"
                    placeholder="Enter your password"
                    size="md"
                    {...form.getInputProps('password')}
                  />

                  {error && (
                    <Alert
                      color="red"
                      icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                      title="Error"
                      variant="light"
                    >
                      {error}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    disabled={isLoading}
                    loading={isLoading}
                    radius="md"
                    size="md"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none'
                    }}
                    type="submit"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </Stack>
              </form>

              <Text c="dimmed" size="sm" ta="center">
                Don&apos;t have an account?{' '}
                <Text
                  c="blue"
                  component="span"
                  style={{ cursor: 'pointer' }}
                  onClick={() => void navigate('/register')}
                >
                  Sign up
                </Text>
              </Text>
            </Stack>
          </Paper>
        </Center>
      </Container>
    </Box>
  );
};

export default Login;
